"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { env } from "next-runtime-env";
import { useEffect, useRef, useState } from "react";
import { AuthSplitLayout } from "@/components/AuthSplitLayout/AuthSplitLayout";
import { useAuth } from "@/lib/authBackend/trimble/tid/context";
import { buildTrialErrorPath, readTrialSignupFailure } from "@/lib/dxTrials/trialSignupFailure";
import { useRegisterTrialPublic } from "./hooks/useRegisterTrialPublic/useRegisterTrialPublic";
import { RegisterTrialPublicForm } from "./RegisterTrialPublicForm";
import { PUBLIC_TRIAL_REDIRECT_DELAY_MS } from "./register-public.constants";
import type { RegisterTrialPublicFormValues } from "./register-public.types";
import { TrimbleTrialRedirectLoader } from "./TrimbleTrialRedirectLoader";
import { buildRegisterPublicRedirect, buildSubmittedTrialHandoffQuery, buildTrialHandoffQuery, readTrialPrefill, readTrialProvisioning, type TrialPrefill, type TrialProvisioning, trialPrefillFromClaims } from "./trialPrefill";
export function RegisterPublicPageContent() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TidTrialRegisterPublicPageContent />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
async function startSignInRedirect(loginWithRedirect: ReturnType<typeof useAuth>["loginWithRedirect"], handoffQuery: string, locale: string) {
  const featureBranchName = env("NEXT_PUBLIC_FEATURE_BRANCH_NAME");
  const {
    email
  } = readTrialPrefill(new URLSearchParams(handoffQuery));
  await loginWithRedirect({
    flow: "trial-signup",
    redirectTo: buildRegisterPublicRedirect(handoffQuery),
    locale,
    ...(featureBranchName ? {
      featureBranch: featureBranchName
    } : {})
  }, {
    loginHint: email
  });
}
function TidTrialRegisterPublicPageContent() {
  const $ = _c(22);
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect
  } = useAuth();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const [failedHandoffQuery, setFailedHandoffQuery] = useState(null);
  const [capturedHandoffQuery, setCapturedHandoffQuery] = useState(null);
  let t0;
  if ($[0] !== searchParams) {
    t0 = buildTrialHandoffQuery(searchParams);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handoffQuery = t0;
  let t1;
  if ($[2] !== searchParams) {
    t1 = async values => {
      setCapturedHandoffQuery(buildSubmittedTrialHandoffQuery(searchParams, values));
    };
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onLeadCaptured = t1;
  let t2;
  let t3;
  if ($[4] !== capturedHandoffQuery || $[5] !== locale || $[6] !== loginWithRedirect) {
    t2 = () => {
      if (capturedHandoffQuery === null) {
        return;
      }
      const timeoutId = window.setTimeout(() => {
        startSignInRedirect(loginWithRedirect, capturedHandoffQuery, locale).catch(() => setFailedHandoffQuery(capturedHandoffQuery));
      }, PUBLIC_TRIAL_REDIRECT_DELAY_MS);
      return () => window.clearTimeout(timeoutId);
    };
    t3 = [capturedHandoffQuery, locale, loginWithRedirect];
    $[4] = capturedHandoffQuery;
    $[5] = locale;
    $[6] = loginWithRedirect;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  if (isLoading) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <AuthSplitLayout><TrimbleTrialRedirectLoader error={false} onRetry={_temp} /></AuthSplitLayout>;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  if (isAuthenticated) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <AuthenticatedTidTrialRegisterPublicPageContent />;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  if (failedHandoffQuery || capturedHandoffQuery) {
    const t4 = Boolean(failedHandoffQuery);
    let t5;
    if ($[11] !== capturedHandoffQuery || $[12] !== failedHandoffQuery || $[13] !== handoffQuery || $[14] !== locale || $[15] !== loginWithRedirect) {
      t5 = () => {
        const retryQuery = failedHandoffQuery ?? capturedHandoffQuery ?? handoffQuery;
        setFailedHandoffQuery(null);
        startSignInRedirect(loginWithRedirect, retryQuery, locale).catch(() => setFailedHandoffQuery(retryQuery));
      };
      $[11] = capturedHandoffQuery;
      $[12] = failedHandoffQuery;
      $[13] = handoffQuery;
      $[14] = locale;
      $[15] = loginWithRedirect;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== t4 || $[18] !== t5) {
      t6 = <AuthSplitLayout><TrimbleTrialRedirectLoader error={t4} onRetry={t5} /></AuthSplitLayout>;
      $[17] = t4;
      $[18] = t5;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    return t6;
  }
  let t4;
  if ($[20] !== onLeadCaptured) {
    t4 = <TidTrialLeadForm onLeadCaptured={onLeadCaptured} />;
    $[20] = onLeadCaptured;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
}
function _temp() {}
function TidTrialLeadForm(t0) {
  const $ = _c(4);
  const {
    onLeadCaptured
  } = t0;
  let t1;
  if ($[0] !== onLeadCaptured) {
    t1 = {
      onLeadCaptured
    };
    $[0] = onLeadCaptured;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const controller = useRegisterTrialPublic(t1);
  let t2;
  if ($[2] !== controller) {
    t2 = <AuthSplitLayout><RegisterTrialPublicForm controller={controller} /></AuthSplitLayout>;
    $[2] = controller;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function AuthenticatedTidTrialRegisterPublicPageContent() {
  const $ = _c(10);
  const {
    getTokens,
    user
  } = useAuth();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = readTrialProvisioning(searchParams);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const provisioning = t0;
  if (!user?.sub) {
    return null;
  }
  if (provisioning) {
    let t1;
    if ($[2] !== getTokens || $[3] !== provisioning) {
      t1 = <TidTrialAutoProvision getTokens={getTokens} provisioning={provisioning} />;
      $[2] = getTokens;
      $[3] = provisioning;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== user) {
    t1 = trialPrefillFromClaims(user);
    $[5] = user;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== getTokens || $[8] !== t1) {
    t2 = <TidTrialRegisterPublicForm getTokens={getTokens} identityPrefill={t1} />;
    $[7] = getTokens;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function TidTrialAutoProvision(t0) {
  const $ = _c(12);
  const {
    getTokens,
    provisioning
  } = t0;
  let t1;
  if ($[0] !== getTokens) {
    t1 = {
      getTokens
    };
    $[0] = getTokens;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const controller = useRegisterTrialPublic(t1);
  const {
    submit
  } = controller;
  const router = useRouter();
  const locale = useLocale();
  const startedRef = useRef(false);
  const {
    company,
    companyRole,
    phone
  } = provisioning;
  let t2;
  let t3;
  if ($[2] !== company || $[3] !== companyRole || $[4] !== locale || $[5] !== phone || $[6] !== router || $[7] !== submit) {
    t2 = () => {
      if (startedRef.current) {
        return;
      }
      const trialFailure = readTrialSignupFailure();
      if (trialFailure) {
        router.replace(`/${locale}${buildTrialErrorPath(trialFailure.supportNotified)}`);
        return;
      }
      startedRef.current = true;
      submit({
        company,
        companyRole,
        phone,
        acceptedTerms: true
      }).catch(_temp2);
    };
    t3 = [submit, company, companyRole, phone, router, locale];
    $[2] = company;
    $[3] = companyRole;
    $[4] = locale;
    $[5] = phone;
    $[6] = router;
    $[7] = submit;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== controller) {
    t4 = <AuthSplitLayout><RegisterTrialPublicForm controller={controller} hideFields={true} /></AuthSplitLayout>;
    $[10] = controller;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp2() {}
function TidTrialRegisterPublicForm(t0) {
  const $ = _c(5);
  const {
    getTokens,
    identityPrefill
  } = t0;
  let t1;
  if ($[0] !== getTokens) {
    t1 = {
      getTokens
    };
    $[0] = getTokens;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const controller = useRegisterTrialPublic(t1);
  let t2;
  if ($[2] !== controller || $[3] !== identityPrefill) {
    t2 = <AuthSplitLayout><RegisterTrialPublicForm controller={controller} identityPrefill={identityPrefill} /></AuthSplitLayout>;
    $[2] = controller;
    $[3] = identityPrefill;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}